import React, { useState } from "react";
import axios from "../utils/axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Link as MuiLink,
} from "@mui/material";
import { Visibility, VisibilityOff, Person } from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import HowToRegIcon from "@mui/icons-material/HowToReg";

const AUTO_LOGOUT_MINUTES = 30; // Thời gian tự động logout (phút)

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Hàm logout tự động
  const scheduleAutoLogout = () => {
    if (window.autoLogoutTimer) clearTimeout(window.autoLogoutTimer);
    window.autoLogoutTimer = setTimeout(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      setMsg("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
      navigate("/login");
    }, AUTO_LOGOUT_MINUTES * 60 * 1000); // 30 phút
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setLoading(true);
    try {
      const res = await axios.post(`/auth/login`, { username, password });
      const { token, user } = res.data;

      // Lưu vào localStorage hoặc sessionStorage tùy chọn
      if (remember) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
      } else {
        sessionStorage.setItem("token", token);
        sessionStorage.setItem("user", JSON.stringify(user));
      }

      scheduleAutoLogout();

      if (onLogin) onLogin(token, user);

      if (user.role === "admin") {
        navigate("/teolucpro");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setMsg("Sai tài khoản hoặc mật khẩu!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 90, damping: 16 }}
      style={{ width: "100%", maxWidth: 380 }}
    >
      <Paper
        elevation={7}
        sx={{
          px: 3,
          py: 3,
          borderRadius: "36px", // Bo tròn lớn như dialog!
          width: 340,
          boxShadow: "0 8px 24px 0 rgba(100,160,255,0.13)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#fff",
          margin: "auto",
        }}
      >
        <Typography
          variant="h5"
          align="center"
          mb={2}
          fontWeight={700}
          sx={{
            color: "#4169e1",
            letterSpacing: 0.5,
            fontSize: 23,
          }}
        >
          Đăng nhập
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <TextField
            label="Tên đăng nhập"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person />
                </InputAdornment>
              ),
            }}
            autoComplete="username"
            size="medium"
            sx={{
              background: "#f8faff",
              borderRadius: 3,
            }}
          />
          <TextField
            label="Mật khẩu"
            type={showPw ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPw((show) => !show)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPw ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            size="medium"
            sx={{
              background: "#f8faff",
              borderRadius: 3,
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                color="primary"
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography fontSize={15}>Ghi nhớ đăng nhập</Typography>}
            sx={{ ml: 0, mb: 0.5 }}
          />
          <motion.div
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            <Button
              variant="contained"
              type="submit"
              size="large"
              sx={{
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 99,
                background: "linear-gradient(90deg, #4f8cff, #7a8cff 80%)",
                ":hover": {
                  background: "linear-gradient(90deg, #7a8cff, #4f8cff 80%)",
                },
                boxShadow: 2,
                mt: 1,
                height: 44,
              }}
              fullWidth
              disabled={loading}
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1,
                    ease: "linear",
                  }}
                  style={{ display: "inline-block" }}
                >
                  <CircularProgress size={22} />
                </motion.div>
              ) : (
                "Đăng nhập"
              )}
            </Button>
          </motion.div>
          {msg && (
            <Alert
              severity="error"
              sx={{
                mt: 1,
                fontSize: "1rem",
                borderRadius: 2,
                fontWeight: 500,
              }}
            >
              {msg}
            </Alert>
          )}
        </Box>
        {/* Link sang trang đăng ký ở dưới cùng */}
        <Box
          mt={2}
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <MuiLink
            component={Link}
            to="/register"
            underline="none"
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#4169e1",
              fontWeight: 500,
              fontSize: 15,
              letterSpacing: 0.1,
              "&:hover": { color: "#2733d4", textDecoration: "underline" },
              transition: "0.2s",
              cursor: "pointer",
              gap: 0.5,
            }}
          >
            <HowToRegIcon sx={{ fontSize: 20, mr: 0.6 }} />
            Bạn chưa có tài khoản? Đăng ký
          </MuiLink>
        </Box>
      </Paper>
    </motion.div>
  );
}

export default LoginForm;
